/**
 * Review and edit the normalised preparation context before practising.
 *
 * Every field says where its value came from: read from the posting by AI
 * (MODEL_INFERRED — the learner is asked to check it) or edited by the learner.
 * Provenance shows what the server kept about the source, and nothing more.
 * Presentational: the parent owns the context.
 */
import { useEffect, useState } from 'react';
import type { PrepContext, PrepLimits } from '../../store/api/interviewPrepApi';
import {
  DEFAULT_PREP_LIMITS,
  ORIGIN_COPY,
  PREP_FIELD_LABELS,
  PREP_LIST_FIELDS,
  describeProvenance,
  editPrepList,
  editPrepText,
  itemsFromText,
  type PrepListField,
} from '../../utils/interviewPrep';

interface Props {
  context: PrepContext;
  onChange: (next: PrepContext) => void;
  limits?: PrepLimits;
  /** How many extracted items the server left out (counts only). */
  droppedItems?: number;
}

function OriginBadge({ origin }: { origin: keyof typeof ORIGIN_COPY }) {
  return (
    <span
      data-origin={origin}
      className={
        origin === 'MODEL_INFERRED'
          ? 'ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800'
          : 'ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700'
      }
    >
      {ORIGIN_COPY[origin]}
    </span>
  );
}

export function PrepContextEditor({ context, onChange, limits = DEFAULT_PREP_LIMITS, droppedItems = 0 }: Props) {
  // Draft text per list, so typing a newline is not undone by re-parsing.
  const fromContext = () =>
    Object.fromEntries(PREP_LIST_FIELDS.map((f) => [f, context[f].items.join('\n')])) as Record<PrepListField, string>;
  const [drafts, setDrafts] = useState<Record<PrepListField, string>>(fromContext);
  useEffect(() => {
    setDrafts(fromContext());
    // A different source, not an edit: reset the drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.provenance.contentHash]);

  return (
    <div className="space-y-4" data-testid="prep-context-editor">
      <div className="text-xs text-gray-600" data-testid="prep-provenance">
        {describeProvenance(context)}
        {context.extraction === 'unavailable' && (
          <span className="block mt-1 text-amber-800" data-status="EXTRACTION_UNAVAILABLE">
            The notes could not be read automatically this time. Fill in what you know; practice works either way.
          </span>
        )}
        {droppedItems > 0 && (
          <span className="block mt-1 text-gray-500" data-testid="prep-dropped">
            {droppedItems} item{droppedItems === 1 ? ' was' : 's were'} left out because {droppedItems === 1 ? 'it' : 'they'} described the
            applicant rather than the work.
          </span>
        )}
      </div>

      {(['roleTitle', 'seniority'] as const).map((field) => (
        <div key={field}>
          <label htmlFor={`prep-${field}`} className="block text-sm font-medium text-gray-700">
            {PREP_FIELD_LABELS[field]}
            {context[field] && <OriginBadge origin={context[field]!.origin} />}
          </label>
          <input
            id={`prep-${field}`}
            type="text"
            value={context[field]?.value ?? ''}
            maxLength={field === 'roleTitle' ? limits.roleTitleMaxChars : limits.seniorityMaxChars}
            onChange={(e) => onChange(editPrepText(context, field, e.target.value))}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
        </div>
      ))}

      {PREP_LIST_FIELDS.map((field) => (
        <div key={field}>
          <label htmlFor={`prep-${field}`} className="block text-sm font-medium text-gray-700">
            {PREP_FIELD_LABELS[field]}
            {context[field].items.length > 0 && <OriginBadge origin={context[field].origin} />}
          </label>
          <textarea
            id={`prep-${field}`}
            rows={Math.min(6, Math.max(2, context[field].items.length + 1))}
            value={drafts[field]}
            onChange={(e) => {
              setDrafts((d) => ({ ...d, [field]: e.target.value }));
              onChange(editPrepList(context, field, itemsFromText(e.target.value)));
            }}
            placeholder="One per line"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
          <p className="mt-0.5 text-[11px] text-gray-500">
            One per line · up to {limits.listMaxItems}, each up to {limits.itemMaxChars} characters
          </p>
        </div>
      ))}
    </div>
  );
}

export default PrepContextEditor;
